import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useAiChat } from '@factorialco/f0-react'

export type VoiceSessionParams = {
  agent: string
  task: string
}

type VoiceControls = {
  start: () => Promise<void>
  stop: () => Promise<void>
}

type AiChatBridge = {
  setMode: Dispatch<SetStateAction<'chat' | 'voice'>>
  setOpen: Dispatch<SetStateAction<boolean>>
}

type VoiceSessionContextValue = {
  params: VoiceSessionParams
  setParams: (params: VoiceSessionParams) => void
  registerControls: (controls: VoiceControls) => () => void
  registerAiChat: (bridge: AiChatBridge) => () => void
  startSession: () => void
}

const DEFAULT_PARAMS: VoiceSessionParams = { agent: '', task: '' }

const VoiceSessionContext = createContext<VoiceSessionContextValue | null>(null)

export function VoiceSessionProvider({ children }: { children: ReactNode }) {
  const [params, setParamsState] = useState<VoiceSessionParams>(DEFAULT_PARAMS)
  const controlsRef = useRef<VoiceControls | null>(null)
  const aiChatRef = useRef<AiChatBridge | null>(null)
  const shouldStartOnRegisterRef = useRef(false)

  const setParams = useCallback((newParams: VoiceSessionParams) => {
    setParamsState(newParams)
  }, [])

  const registerControls = useCallback((controls: VoiceControls): () => void => {
    controlsRef.current = controls

    if (shouldStartOnRegisterRef.current) {
      shouldStartOnRegisterRef.current = false
      void controls.start()
    }

    return () => {
      controlsRef.current = null
    }
  }, [])

  const registerAiChat = useCallback((bridge: AiChatBridge): () => void => {
    aiChatRef.current = bridge
    bridge.setMode('chat')
    return () => {
      aiChatRef.current = null
    }
  }, [])

  const startSession = useCallback(() => {
    aiChatRef.current?.setOpen(true)
    aiChatRef.current?.setMode('voice')

    if (controlsRef.current) {
      void controlsRef.current.start()
      return
    }

    shouldStartOnRegisterRef.current = true
  }, [])

  return (
    <VoiceSessionContext.Provider
      value={{
        params,
        setParams,
        registerControls,
        registerAiChat,
        startSession,
      }}
    >
      {children}
    </VoiceSessionContext.Provider>
  )
}

export function VoiceSessionInit() {
  const { open, mode, setMode, setOpen } = useAiChat()
  const { registerAiChat } = useVoiceSession()

  useEffect(() => {
    return registerAiChat({ setMode, setOpen })
  }, [registerAiChat, setMode, setOpen])

  useEffect(() => {
    if (!open && mode !== 'chat') {
      setMode('chat')
    }
  }, [mode, open, setMode])

  return null
}

export function useVoiceSession(): VoiceSessionContextValue {
  const context = useContext(VoiceSessionContext)
  if (!context) {
    throw new Error('useVoiceSession must be used within a VoiceSessionProvider')
  }
  return context
}
